/**
 * Chunked Chat Integration
 * Integrates chunked audio processing with the existing chat interface
 * Handles both single and chunked processing modes seamlessly
 */

class ChunkedChatIntegration {
    constructor() {
        this.chunkedPlayer = new ChunkedAudioPlayer();
        this.currentMode = 'single'; // 'single' or 'chunked'
        this.currentRequestId = null;
        
        // Audio elements for single mode (existing functionality)
        this.singleAudioElement = null;
        
        // Volume control
        this.volumeController = null;
        
        // Bind event handlers
        this.setupChunkedPlayerEvents();
        
        console.log('ChunkedChatIntegration initialized');
    }
    
    /**
     * Set the volume controller reference
     * @param {VolumeController} controller - The volume controller to use for audio volume
     */
    setVolumeController(controller) {
        this.volumeController = controller;
        
        // Pass volume controller to chunked player
        if (this.chunkedPlayer) {
            this.chunkedPlayer.setVolumeController(controller);
        }
        
        console.log('Volume controller connected to ChunkedChatIntegration');
    }
    
    /**
     * Setup event handlers for the chunked audio player
     */
    setupChunkedPlayerEvents() {
        this.chunkedPlayer.onChunkReady = (chunkIndex, chunkData) => {
            console.log(`Chunk ${chunkIndex} ready for playback`);
            // Update UI to show chunk progress if needed
            this.updateChunkProgress(chunkIndex, chunkData);
        };
        
        this.chunkedPlayer.onPlaybackStart = () => {
            console.log('Chunked playback started');
            // Update UI to show audio is playing
            this.updateAudioStatus('playing');
        };
        
        this.chunkedPlayer.onPlaybackEnd = () => {
            console.log('Chunked playback completed');
            // Update UI to show audio finished
            this.updateAudioStatus('completed');
        };
        
        this.chunkedPlayer.onError = (error) => {
            console.error('Chunked playback error:', error);
            // Update UI to show error state
            this.updateAudioStatus('error');
        };
    }
    
    /**
     * Handle chat response and initiate appropriate audio processing
     * @param {Object} chatResponse - Response from the chat API
     */
    async handleChatResponse(chatResponse) {
        this.currentRequestId = chatResponse.request_id;
        this.currentMode = chatResponse.processing_mode || 'single';
        
        console.log(`Processing chat response in ${this.currentMode} mode`);
        
        if (this.currentMode === 'chunked') {
            await this.handleChunkedResponse(chatResponse);
        } else {
            await this.handleSingleResponse(chatResponse);
        }
    }
    
    /**
     * Handle chunked audio processing response
     * @param {Object} chatResponse - Chat response with chunked processing info
     */
    async handleChunkedResponse(chatResponse) {
        try {
            console.log(`Starting chunked processing for ${chatResponse.total_chunks} chunks`);
            
            // Update UI to show chunked processing has started
            this.updateAudioStatus('chunked_processing');
            this.updateChunkedInfo(chatResponse.total_chunks, chatResponse.first_chunk_text);
            
            // Start chunked playback
            await this.chunkedPlayer.startChunkedPlayback(this.currentRequestId, {
                total_chunks: chatResponse.total_chunks,
                first_chunk_text: chatResponse.first_chunk_text
            });
            
        } catch (error) {
            console.error('Error handling chunked response:', error);
            this.updateAudioStatus('error');
        }
    }
    
    /**
     * Handle single audio processing response (existing functionality)
     * @param {Object} chatResponse - Chat response with single processing info
     */
    async handleSingleResponse(chatResponse) {
        try {
            console.log('Processing single audio response');
            
            // Update UI to show processing has started
            this.updateAudioStatus('processing');
            
            // Poll for audio completion (existing functionality)
            await this.pollForAudioCompletion(this.currentRequestId);
            
        } catch (error) {
            console.error('Error handling single response:', error);
            this.updateAudioStatus('error');
        }
    }
    
    /**
     * Poll for audio completion in single mode
     * @param {string} requestId - Request ID to poll for
     */
    async pollForAudioCompletion(requestId) {
        const maxAttempts = 30;
        let attempts = 0;
        
        const poll = async () => {
            attempts++;
            
            try {
                // Check ElevenLabs status
                const statusResponse = await fetch(`/api/elevenlabs-status/${requestId}`);
                const statusData = await statusResponse.json();
                
                if (statusData.status === 'completed') {
                    console.log('Single audio processing completed');
                    await this.playSingleAudio(statusData);
                    return;
                }
                
                if (statusData.status === 'error') {
                    console.error('Audio processing error:', statusData);
                    this.updateAudioStatus('error');
                    return;
                }
                
                // Continue polling if still processing
                if (attempts < maxAttempts) {
                    setTimeout(poll, 1000);
                } else {
                    console.error('Audio processing timeout');
                    this.updateAudioStatus('timeout');
                }
                
            } catch (error) {
                console.error('Error polling for audio completion:', error);
                if (attempts < maxAttempts) {
                    setTimeout(poll, 2000);
                } else {
                    this.updateAudioStatus('error');
                }
            }
        };
        
        poll();
    }
    
    /**
     * Play single audio (existing functionality enhanced)
     * @param {Object} statusData - Audio status data
     */
    async playSingleAudio(statusData) {
        try {
            // Get audio data from ElevenLabs response
            const lipsyncResponse = await fetch(`/api/elevenlabs-lipsync/${this.currentRequestId}`);
            const lipsyncData = await lipsyncResponse.json();
            
            if (lipsyncData.status === 'success' && lipsyncData.audio_base64) {
                // Create audio from base64
                const audioBlob = this.base64ToBlob(lipsyncData.audio_base64, 'audio/mpeg');
                const audioUrl = URL.createObjectURL(audioBlob);
                
                this.singleAudioElement = new Audio(audioUrl);
                
                // Apply volume control if available
                if (this.volumeController) {
                    this.volumeController.applyVolumeToAudio(this.singleAudioElement);
                }
                
                this.singleAudioElement.addEventListener('canplaythrough', () => {
                    console.log('Single audio ready for playback');
                    this.updateAudioStatus('ready');
                });
                
                this.singleAudioElement.addEventListener('play', () => {
                    this.updateAudioStatus('playing');
                });
                
                this.singleAudioElement.addEventListener('ended', () => {
                    this.updateAudioStatus('completed');
                    URL.revokeObjectURL(audioUrl);
                });
                
                this.singleAudioElement.addEventListener('error', (e) => {
                    console.error('Single audio playback error:', e);
                    this.updateAudioStatus('error');
                });
                
                // Auto-play the audio
                await this.singleAudioElement.play();
                
            } else {
                console.error('No audio data available for single playback');
                this.updateAudioStatus('error');
            }
            
        } catch (error) {
            console.error('Error playing single audio:', error);
            this.updateAudioStatus('error');
        }
    }
    
    /**
     * Update audio status in the UI
     * @param {string} status - Current audio status
     */
    updateAudioStatus(status) {
        console.log(`Audio status: ${status}`);
        
        // Find status elements in the UI
        const statusElements = document.querySelectorAll('.audio-status, .processing-status');
        
        statusElements.forEach(element => {
            switch (status) {
                case 'processing':
                    element.textContent = 'Processing audio...';
                    element.className = 'audio-status processing';
                    break;
                case 'chunked_processing':
                    element.textContent = 'Processing chunks...';
                    element.className = 'audio-status chunked-processing';
                    break;
                case 'ready':
                    element.textContent = 'Audio ready';
                    element.className = 'audio-status ready';
                    break;
                case 'playing':
                    element.textContent = 'Playing audio';
                    element.className = 'audio-status playing';
                    break;
                case 'completed':
                    element.textContent = 'Audio completed';
                    element.className = 'audio-status completed';
                    break;
                case 'error':
                    element.textContent = 'Audio error';
                    element.className = 'audio-status error';
                    break;
                case 'timeout':
                    element.textContent = 'Audio timeout';
                    element.className = 'audio-status timeout';
                    break;
            }
        });
        
        // Dispatch custom event for other components
        window.dispatchEvent(new CustomEvent('audioStatusChange', {
            detail: { status, mode: this.currentMode, requestId: this.currentRequestId }
        }));
    }
    
    /**
     * Update chunked processing information in the UI
     * @param {number} totalChunks - Total number of chunks
     * @param {string} firstChunkText - Text of the first chunk
     */
    updateChunkedInfo(totalChunks, firstChunkText) {
        console.log(`Chunked info: ${totalChunks} chunks, first: "${firstChunkText.substring(0, 50)}..."`);
        
        // Update UI elements that show chunked processing info
        const chunkInfoElements = document.querySelectorAll('.chunk-info');
        chunkInfoElements.forEach(element => {
            element.innerHTML = `
                <div class="chunk-progress">
                    <span>Processing ${totalChunks} chunks</span>
                    <div class="chunk-preview">${firstChunkText.substring(0, 100)}...</div>
                </div>
            `;
        });
    }
    
    /**
     * Update chunk progress in the UI
     * @param {number} chunkIndex - Current chunk index
     * @param {Object} chunkData - Chunk data
     */
    updateChunkProgress(chunkIndex, chunkData) {
        console.log(`Chunk progress: ${chunkIndex + 1}/${this.chunkedPlayer.totalChunks}`);
        
        // Update progress bars or indicators
        const progressElements = document.querySelectorAll('.chunk-progress-bar');
        progressElements.forEach(element => {
            const progress = ((chunkIndex + 1) / this.chunkedPlayer.totalChunks) * 100;
            element.style.width = `${progress}%`;
        });
        
        // Update chunk text display
        const chunkTextElements = document.querySelectorAll('.current-chunk-text');
        chunkTextElements.forEach(element => {
            element.textContent = chunkData.chunk_text;
        });
    }
    
    /**
     * Control playback (pause/resume/stop)
     * @param {string} action - Action to perform ('pause', 'resume', 'stop')
     */
    controlPlayback(action) {
        if (this.currentMode === 'chunked') {
            switch (action) {
                case 'pause':
                    this.chunkedPlayer.pause();
                    break;
                case 'resume':
                    this.chunkedPlayer.resume();
                    break;
                case 'stop':
                    this.chunkedPlayer.stop();
                    break;
            }
        } else if (this.singleAudioElement) {
            switch (action) {
                case 'pause':
                    this.singleAudioElement.pause();
                    break;
                case 'resume':
                    this.singleAudioElement.play();
                    break;
                case 'stop':
                    this.singleAudioElement.pause();
                    this.singleAudioElement.currentTime = 0;
                    break;
            }
        }
    }
    
    /**
     * Get current playback status
     * @returns {Object} Current playback status
     */
    getPlaybackStatus() {
        if (this.currentMode === 'chunked') {
            return this.chunkedPlayer.getStatus();
        } else if (this.singleAudioElement) {
            return {
                isPlaying: !this.singleAudioElement.paused,
                isPaused: this.singleAudioElement.paused,
                currentTime: this.singleAudioElement.currentTime,
                duration: this.singleAudioElement.duration,
                mode: 'single',
                requestId: this.currentRequestId
            };
        }
        
        return { mode: 'none', isPlaying: false };
    }
    
    /**
     * Clean up resources
     */
    cleanup() {
        if (this.currentMode === 'chunked') {
            this.chunkedPlayer.cleanup();
        }
        
        if (this.singleAudioElement) {
            this.singleAudioElement.pause();
            if (this.singleAudioElement.src) {
                URL.revokeObjectURL(this.singleAudioElement.src);
            }
            this.singleAudioElement = null;
        }
        
        this.currentRequestId = null;
        this.currentMode = 'single';
    }
    
    /**
     * Convert base64 string to Blob
     * @param {string} base64 - Base64 encoded string
     * @param {string} mimeType - MIME type of the data
     * @returns {Blob} - Blob object
     */
    base64ToBlob(base64, mimeType) {
        const byteCharacters = atob(base64);
        const byteArrays = [];
        
        for (let offset = 0; offset < byteCharacters.length; offset += 512) {
            const slice = byteCharacters.slice(offset, offset + 512);
            const byteNumbers = new Array(slice.length);
            
            for (let i = 0; i < slice.length; i++) {
                byteNumbers[i] = slice.charCodeAt(i);
            }
            
            const byteArray = new Uint8Array(byteNumbers);
            byteArrays.push(byteArray);
        }
        
        return new Blob(byteArrays, { type: mimeType });
    }
}

// Create global instance
window.chunkedChatIntegration = new ChunkedChatIntegration();

// Export for use in other modules
window.ChunkedChatIntegration = ChunkedChatIntegration;